import { create } from 'zustand'

export type TextPhase = 'commentary' | 'final_answer'

export type AiBlock =
  | { type: 'text'; text: string; phase?: TextPhase }
  | { type: 'thinking'; text: string }
  | { type: 'tool_call'; name: string; input?: Record<string, unknown> | null }
  | { type: 'tool_result'; content: string; isError: boolean }

export interface UserImageAttachment {
  mimeType: string
  dataBase64: string
}

export interface ChatMessage {
  id: string
  dbId?: string
  role: 'user' | 'ai'
  content: string
  blocks?: AiBlock[]
  attachments?: UserImageAttachment[]
  feedback?: 'up' | 'down' | null
  interrupted?: boolean
  timestamp: number
}

export interface ArtifactRef {
  id: string
  title: string
}

export interface Conversation {
  id: string
  title: string | null
  sourceTemplateVersionId: string | null
  createdAt: string
  updatedAt: string
  messageCount: number
  resultCount: number
  chartCount: number
  lastMessageContent: string
  chartArtifacts: ArtifactRef[]
  resultArtifacts: ArtifactRef[]
}

export interface DataSource {
  id: string
  name: string
  displayName: string
  sourceType: 'csv_upload' | 'api_fetcher' | 'builtin' | 'staging'
  columns: { name: string; dtype: string }[]
  rowCount: number | null
  dateColumn: string
  dateRange: { min: string; max: string } | null
  fetcherUrl: string | null
  description: string | null
}

export type ActivePage = 'chat' | 'data-lake' | 'artifacts' | 'workflows' | 'theses' | 'trades' | 'analysts' | 'whitepapers' | 'models' | 'notifications'
type ConnectionStatus = 'disconnected' | 'connecting' | 'provisioning' | 'sandbox_ready' | 'thinking' | 'connected'

export interface PendingFieldEdit {
  fieldId: string
  label: string
  intent: string | null
  seq: number
}

interface ChatState {
  activePage: ActivePage
  sidebarOpen: boolean
  sidebarCollapsed: boolean

  conversations: Conversation[]
  activeConversationId: string | null
  messagesById: Record<string, ChatMessage[]>

  analystBuilderConversationId: string | null

  dataSources: DataSource[]
  conversationDataSources: Record<string, DataSource[]>

  pendingClaimChatId: string | null

  pendingFirstMessage: {
    conversationId: string
    text: string
    attachments?: Array<{ mime_type: string; data_base64: string }>
  } | null

  failedArtifacts: Record<string, 'chart' | 'result' | 'spreadsheet'>

  connectionStatus: ConnectionStatus
  isStreaming: boolean

  pendingFieldEdit: PendingFieldEdit | null
  builderTurnActive: boolean

  setActivePage: (page: ActivePage) => void
  setSidebarOpen: (open: boolean) => void
  setSidebarCollapsed: (collapsed: boolean) => void
  setPendingClaimChatId: (id: string | null) => void
  setPendingFirstMessage: (
    val: {
      conversationId: string
      text: string
      attachments?: Array<{ mime_type: string; data_base64: string }>
    } | null,
  ) => void

  setConversations: (convs: Conversation[]) => void
  setActiveConversation: (id: string | null) => void
  addConversation: (conv: Conversation) => void
  updateConversationTitle: (id: string, title: string) => void

  setDataSources: (ds: DataSource[]) => void
  addDataSource: (ds: DataSource) => void
  removeDataSource: (name: string) => void
  setConversationDataSources: (conversationId: string, sources: DataSource[]) => void

  setAnalystBuilderConversationId: (id: string | null) => void

  setMessages: (conversationId: string, msgs: ChatMessage[]) => void
  addMessage: (conversationId: string, msg: Omit<ChatMessage, 'id' | 'timestamp'>) => string
  setMessageFeedback: (messageId: string, feedback: 'up' | 'down' | null) => void
  appendTextToLastAi: (conversationId: string, text: string, phase?: TextPhase) => void
  addThinkingToLastAi: (conversationId: string, text: string) => void
  addToolCallToLastAi: (conversationId: string, name: string, input: Record<string, unknown> | null) => void
  addToolResultToLastAi: (conversationId: string, content: string, isError: boolean) => void
  markLastAiInterrupted: (conversationId: string) => void
  removeLastAiIfEmpty: (conversationId: string) => void
  clearMessages: (conversationId: string) => void

  setConnectionStatus: (status: ConnectionStatus) => void
  setIsStreaming: (streaming: boolean) => void

  setPendingFieldEdit: (val: PendingFieldEdit | null) => void
  setBuilderTurnActive: (active: boolean) => void

  recordFailedArtifact: (kind: 'chart' | 'result' | 'spreadsheet', id: string) => void
}

let _nextId = 0
const genId = () => `msg-${Date.now()}-${_nextId++}`

const SIDEBAR_COLLAPSED_KEY = 'golf.sidebarCollapsed'
function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}
function writeSidebarCollapsed(v: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, v ? '1' : '0')
  } catch {
    void 0
  }
}


function updateLastAi(msgs: ChatMessage[], updater: (msg: ChatMessage) => ChatMessage): ChatMessage[] {
  const copy = [...msgs]
  const last = copy[copy.length - 1]
  if (last?.role === 'ai') {
    copy[copy.length - 1] = updater(last)
  }
  return copy
}

function writeMessages(state: ChatState, conversationId: string, msgs: ChatMessage[]): Partial<ChatState> {
  return {
    messagesById: {
      ...state.messagesById,
      [conversationId]: msgs,
    },
  }
}

export const useChatStore = create<ChatState>()((set) => ({
  activePage: 'chat' as ActivePage,
  sidebarOpen: false,
  sidebarCollapsed: readSidebarCollapsed(),
  conversations: [],
  activeConversationId: null,
  messagesById: {},
  analystBuilderConversationId: null,
  dataSources: [],
  conversationDataSources: {},
  pendingClaimChatId: null,
  pendingFirstMessage: null,
  failedArtifacts: {},
  connectionStatus: 'disconnected',
  isStreaming: false,
  pendingFieldEdit: null,
  builderTurnActive: false,

  setActivePage: (activePage) => set({ activePage }),
  setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
  setSidebarCollapsed: (sidebarCollapsed) => {
    writeSidebarCollapsed(sidebarCollapsed)
    set({ sidebarCollapsed })
  },
  setPendingClaimChatId: (pendingClaimChatId) => set({ pendingClaimChatId }),
  setPendingFirstMessage: (pendingFirstMessage) => set({ pendingFirstMessage }),

  setConversations: (conversations) => set({ conversations }),

  setActiveConversation: (id) => set({ activeConversationId: id }),

  addConversation: (conv) =>
    set((s) => ({ conversations: [conv, ...s.conversations] })),

  updateConversationTitle: (id, title) =>
    set((s) => ({
      conversations: s.conversations.map((c) =>
        c.id === id ? { ...c, title } : c
      ),
    })),

  setDataSources: (dataSources) => set({ dataSources }),

  addDataSource: (ds) =>
    set((s) => ({ dataSources: [...s.dataSources, ds] })),

  removeDataSource: (name) =>
    set((s) => ({
      dataSources: s.dataSources.filter((d) => d.name !== name),
    })),

  setConversationDataSources: (conversationId, sources) =>
    set((s) => ({
      conversationDataSources: {
        ...s.conversationDataSources,
        [conversationId]: sources,
      },
    })),

  setAnalystBuilderConversationId: (analystBuilderConversationId) =>
    set({ analystBuilderConversationId }),

  setMessages: (conversationId, msgs) =>
    set((s) => ({
      messagesById: { ...s.messagesById, [conversationId]: msgs },
    })),

  setMessageFeedback: (messageId, feedback) =>
    set((s) => {
      const next: Record<string, ChatMessage[]> = {}
      let touched = false
      for (const [cid, msgs] of Object.entries(s.messagesById)) {
        if (msgs.some((m) => m.id === messageId)) {
          next[cid] = msgs.map((m) => (m.id === messageId ? { ...m, feedback } : m))
          touched = true
        } else {
          next[cid] = msgs
        }
      }
      return touched ? { messagesById: next } : {}
    }),

  addMessage: (conversationId, msg) => {
    const id = genId()
    set((s) => {
      const current = s.messagesById[conversationId] ?? []
      return writeMessages(s, conversationId, [
        ...current,
        { ...msg, id, timestamp: Date.now() },
      ])
    })
    return id
  },

  appendTextToLastAi: (conversationId, text, phase) =>
    set((s) => {
      const msgs = s.messagesById[conversationId] ?? []
      const updated = updateLastAi(msgs, (msg) => {
        const blocks = [...(msg.blocks ?? [])]
        const lastBlock = blocks[blocks.length - 1]
        if (lastBlock?.type === 'text' && lastBlock.phase === phase) {
          blocks[blocks.length - 1] = { ...lastBlock, text: lastBlock.text + text }
        } else {
          const block: AiBlock = { type: 'text', text }
          if (phase) block.phase = phase
          blocks.push(block)
        }
        return { ...msg, content: msg.content + text, blocks }
      })
      return writeMessages(s, conversationId, updated)
    }),

  addThinkingToLastAi: (conversationId, text) =>
    set((s) => {
      const msgs = s.messagesById[conversationId] ?? []
      const updated = updateLastAi(msgs, (msg) => {
        const blocks = [...(msg.blocks ?? [])]
        const lastBlock = blocks[blocks.length - 1]
        if (lastBlock?.type === 'thinking') {
          blocks[blocks.length - 1] = { ...lastBlock, text: lastBlock.text + text }
        } else {
          blocks.push({ type: 'thinking', text })
        }
        return { ...msg, blocks }
      })
      return writeMessages(s, conversationId, updated)
    }),

  addToolCallToLastAi: (conversationId, name, input) =>
    set((s) => {
      const msgs = s.messagesById[conversationId] ?? []
      const updated = updateLastAi(msgs, (msg) => {
        const blocks = [...(msg.blocks ?? [])]
        blocks.push({ type: 'tool_call', name, input })
        return { ...msg, blocks }
      })
      return writeMessages(s, conversationId, updated)
    }),

  addToolResultToLastAi: (conversationId, content, isError) =>
    set((s) => {
      const msgs = s.messagesById[conversationId] ?? []
      const updated = updateLastAi(msgs, (msg) => {
        const blocks = [...(msg.blocks ?? [])]
        blocks.push({ type: 'tool_result', content, isError })
        return { ...msg, blocks }
      })
      return writeMessages(s, conversationId, updated)
    }),

  markLastAiInterrupted: (conversationId) =>
    set((s) => {
      const msgs = s.messagesById[conversationId] ?? []
      const updated = updateLastAi(msgs, (msg) => ({ ...msg, interrupted: true }))
      return writeMessages(s, conversationId, updated)
    }),

  removeLastAiIfEmpty: (conversationId) =>
    set((s) => {
      const msgs = s.messagesById[conversationId] ?? []
      const last = msgs[msgs.length - 1]
      if (!last || last.role !== 'ai') return {}
      const hasContent = last.content.length > 0 || (last.blocks && last.blocks.length > 0)
      if (hasContent) return {}
      return writeMessages(s, conversationId, msgs.slice(0, -1))
    }),

  clearMessages: (conversationId) =>
    set((s) => writeMessages(s, conversationId, [])),

  setConnectionStatus: (connectionStatus) => set({ connectionStatus }),
  setIsStreaming: (isStreaming) => set({ isStreaming }),

  setPendingFieldEdit: (pendingFieldEdit) => set({ pendingFieldEdit }),
  setBuilderTurnActive: (builderTurnActive) => set({ builderTurnActive }),

  recordFailedArtifact: (kind, id) =>
    set((s) => ({
      failedArtifacts: { ...s.failedArtifacts, [id]: kind },
    })),
}))
